<template>
  <div :class="['picker-controls', { 'year-mode': type === 'year' }]">
    <template v-if="['month', 'season'].includes(type)">
      <div class="month-season-control">
        <div class="controls-prev">
          <v-btn
            v-if="type === 'month'"
            data-testid="month-prev"
            variant="text"
            density="compact"
            class="control-btn"
            :disabled="!canShiftMonthPrev"
            @click="$emit('shiftMonth', -1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            v-if="type === 'season'"
            data-testid="season-prev"
            variant="text"
            density="compact"
            class="control-btn"
            :disabled="!canShiftSeasonPrev"
            @click="$emit('shiftSeason', -1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </div>
        <div class="selected-values">
          <v-btn
            v-if="type === 'month'"
            data-testid="month-btn"
            variant="text"
            density="compact"
            @click="$emit('toggleView', 'month')"
          >
            {{ formattedMonth }}
            <v-icon>mdi-menu-down</v-icon>
          </v-btn>
          <v-btn
            v-if="type === 'season'"
            data-testid="season-btn"
            variant="text"
            density="compact"
            @click="$emit('toggleView', 'season')"
          >
            {{ formattedSeason }}
            <v-icon>mdi-menu-down</v-icon>
          </v-btn>
        </div>
        <div class="controls-next">
          <v-btn
            v-if="type === 'month'"
            data-testid="month-next"
            variant="text"
            density="compact"
            class="control-btn"
            :disabled="!canShiftMonthNext"
            @click="$emit('shiftMonth', 1)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn
            v-if="type === 'season'"
            data-testid="season-next"
            variant="text"
            density="compact"
            class="control-btn"
            :disabled="!canShiftSeasonNext"
            @click="$emit('shiftSeason', 1)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="year-control">
        <div class="controls-prev">
          <v-btn
            data-testid="year-prev"
            variant="text"
            density="compact"
            class="control-btn"
            :disabled="!canShiftViewYearPrev"
            @click="$emit('shiftViewYear', -1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </div>
        <div class="selected-values">
          <v-btn
            data-testid="year-btn"
            variant="text"
            density="compact"
            @click="$emit('toggleView', 'year')"
          >
            {{ formattedYear }}
            <v-icon>mdi-menu-down</v-icon>
          </v-btn>
        </div>
        <div class="controls-next">
          <v-btn
            data-testid="year-next"
            variant="text"
            density="compact"
            class="control-btn"
            :disabled="!canShiftViewYearNext"
            @click="$emit('shiftViewYear', 1)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="year-control">
        <div class="controls-prev">
          <v-btn
            v-if="type === 'year'"
            data-testid="year-prev"
            variant="text"
            density="compact"
            class="control-btn"
            :disabled="!canShiftYearPrev"
            @click="$emit('shiftYear', -1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </div>
        <div class="selected-values">
          <v-btn
            data-testid="year-btn"
            variant="text"
            density="compact"
            @click="$emit('toggleView', 'year')"
          >
            {{ formattedYear }}
          </v-btn>
        </div>
        <div class="controls-next">
          <v-btn
            v-if="type === 'year'"
            data-testid="year-next"
            variant="text"
            density="compact"
            class="control-btn"
            :disabled="!canShiftYearNext"
            @click="$emit('shiftYear', 1)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'PickerControls',
  props: {
    type: {
      type: String as () => 'year' | 'month' | 'season',
      required: true,
    },
    formattedYear: { type: Number, required: true },
    formattedMonth: { type: String, default: '' },
    formattedSeason: { type: String, default: '' },
    canShiftMonthPrev: Boolean,
    canShiftMonthNext: Boolean,
    canShiftSeasonPrev: Boolean,
    canShiftSeasonNext: Boolean,
    canShiftYearPrev: Boolean,
    canShiftYearNext: Boolean,
    canShiftViewYearPrev: Boolean,
    canShiftViewYearNext: Boolean,
    testid: {
      type: String,
      required: true,
    },
  },
  emits: ['shiftYear', 'shiftViewYear', 'shiftMonth', 'shiftSeason', 'toggleView'],
});
</script>

<style scoped>
.picker-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 350px;
  height: 56px;
  gap: 0.5rem;
  flex-wrap: nowrap;
  padding: 0.8rem;
}

.controls-prev,
.selected-values,
.controls-next {
  display: flex;
  align-items: center;
}

.control-btn {
  width: 36px;
  min-width: 36px;
}

.picker-controls.year-mode {
  justify-content: center;
}

.month-season-control,
.year-control {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.selected-values {
  text-align: center;
}

.selected-values .v-btn {
  letter-spacing: normal;
  text-transform: none;
}
.v-btn {
  padding: 0;
}
</style>
